<template>
  <div class="membership-card">
    <div>
      <div class="d-flex mb-2">
        <TextField
          v-model="formData.name"
          name="name"
          label="Program Name"
          placeholder="Program Name"
          :validation-errors="v$.$errors"
          label-class="flex-2"
          class="mr-3 full-width"
        />
      </div>
      <div
        class="membership-additional-info mb-2 flex flex-col md:flex-row gap-3"
      >
        <TextField
          v-model="price"
          name="price"
          label="Program Price"
          placeholder="Program Price"
          :validation-errors="v$.$errors"
          label-class="flex-1"
          class="mr-3"
          currency
        />
        <TextField
          v-model="additionalUnitPrice"
          label="Additional Systems"
          placeholder="Additional Systems"
          :validation-errors="v$.$errors"
          label-class="flex-1"
          class="mr-3"
          currency
        />
        <div class="flex gap-3">
          <label class="block form-group">
            <span class="label">Duration</span>
            <PeriodSelector
              :frequency="formData.duration"
              class="mr-3"
              name="Period"
              :validation-errors="v$.$errors"
            />
          </label>
          <div class="flex flex-col items-center justify-start">
            <span class="label">Subscription</span>
            <Checkbox
              v-model="formData.auto_renews"
              class="mt-2"
              :disabled="!!currentMembershipProgram"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="mb-3">
      <sera-checkbox
        :checked="formData.taxable"
        @change="({ detail }) => (formData.taxable = detail)"
      >
        <strong>Taxable (original sale)</strong>
      </sera-checkbox>
    </div>
    <div class="mb-3">
      <sera-checkbox
        :checked="formData.taxable_on_renewal"
        @change="({ detail }) => (formData.taxable_on_renewal = detail)"
      >
        <strong>Taxable (renewal)</strong>
      </sera-checkbox>
    </div>
    <label class="block form-group">
      <span class="label">Description</span>
      <textarea
        v-model="formData.description"
        class="form-control"
        rows="5"
        placeholder="Description"
      />
    </label>
    <div>
      <label class="block form-group">
        <div class="label mb-2">Included Tasks</div>
        <MembershipProgramTaskRow
          v-for="item in formData.items"
          :key="item.item_id"
          :item="item"
          data-cy="taskRow"
          @delete-item="deleteItemFromMembership"
        />
        <ItemAdder
          class="adder full-width"
          slim
          text="ADD TASK"
          @click="toggleTaskModal"
        />
        <MembershipTaskPickerWrapper
          v-if="taskModal"
          @hide-modal="toggleTaskModal"
          @add-task="addTaskToMembership"
        />
      </label>
    </div>
    <Select
      v-if="showAccountingCategories"
      v-model="formData.accounting_category_id"
      name="Default Accounting Category"
      :options="accountingCategoriesOptions"
      placeholder="Select Default Accounting Category"
      :validation-errors="v$.$errors"
      class="mb-4"
    />
    <Select
      v-if="showAccountingClasses"
      v-model="formData.accounting_class_id"
      name="Default Accounting Class"
      :options="accountingClassOptions"
      placeholder="Select Default Accounting Class"
      :validation-errors="v$.$errors"
      class="mb-4"
    >
      <template #item-row="{ option }">
        <div class="option-row">
          <span :class="{ 'strike-through': option.archived }">
            {{ option.label }}
          </span>
        </div>
      </template>
    </Select>
    <div class="mb-4">
      <div class="label mb-2 d-flex">
        <span>Department Pricing</span>
        <span>
          <Tooltip>
            <template #trigger>
              <i class="fas fa-question-circle text-blue ml-1"></i>
            </template>
            <template #tooltip>
              <span>
                Select the price sheet to use per department. Default is<br />
                “Standard Price Sheet” and will auto adjust to be the<br />
                standard price sheet.
              </span>
            </template>
          </Tooltip>
        </span>
      </div>
    </div>
    <div class="d-flex full-width department-container">
      <label
        v-for="(dept, i) in formData.price_sheets"
        :key="dept.department_id"
        class="form-group flex-1 price-sheet"
        :class="{ 'last-pricesheet': isLastOnRow(i) }"
      >
        <span class="label mb-2">{{ dept.department_name }}</span>
        <Select
          :options="getDeptPriceSheetOptions(dept.department_id)"
          :value="dept.department_membership_price_sheet_id"
          @update:modelValue="
            ($event) => handleDeptChange(dept.department_id, $event)
          "
        />
      </label>
    </div>
    <div class="button-container mt-3">
      <Button class="mr-5" type="secondary" @click="returnToMembershipsPage()">
        <span>Cancel</span>
      </Button>
      <Button
        :disabled="saving"
        data-cy="save-membership-program-button"
        @click="handleSave()"
      >
        <span v-if="!saving">Save Membership</span>
        <Spinner v-else message="Saving..." />
      </Button>
    </div>
  </div>
</template>

<script>
import Select from "serviceshift-ui/components/Inputs/Select.vue";
import { mapActions, mapGetters, mapState } from "vuex";

import Button from "@/components/Button.vue";
import Checkbox from "@/components/Checkbox.vue";
import ItemAdder from "@/components/ItemAdder.vue";
import Spinner from "@/components/Spinner.vue";
import TextField from "@/components/TextField.vue";
import Tooltip from "@/components/Tooltip.vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import MembershipProgramTaskRow from "./MembershipProgramTaskRow.vue";
import MembershipTaskPickerWrapper from "./MembershipTaskPickerWrapper.vue";
import PeriodSelector from "./PeriodSelector.vue";

export default {
  components: {
    Spinner,
    Button,
    TextField,
    ItemAdder,
    MembershipTaskPickerWrapper,
    MembershipProgramTaskRow,
    Tooltip,
    Checkbox,
    PeriodSelector,
    Select
  },
  data() {
    return {
      saving: false,
      formData: {
        taxable: true,
        taxable_on_renewal: true,
        items: [],
        duration: { count: 1, period: "year" }
      },
      price: 0,
      taskModal: false,
      additionalUnitPrice: 0,
      membershipTypeOptions: [
        {
          value: "Monthly",
          label: "Monthly"
        },
        {
          value: "Annual",
          label: "Annual"
        }
      ]
    };
  },
  computed: {
    ...mapGetters([
      "accountingClassOptions",
      "showAccountingCategories",
      "showAccountingClasses"
    ]),
    ...mapState([
      "currentMembershipProgram",
      "priceSheets",
      "departments",
      "accountingCategories"
    ]),
    ...mapState({
      accountingCategoriesOptions: ({ accountingCategories }) =>
        accountingCategories.map((accountingCategory) => ({
          value: accountingCategory.id,
          label: accountingCategory.name
        }))
    })
  },
  watch: {
    currentMembershipProgram(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setFormData();
      }
    }
  },
  async beforeMount() {
    // Need to fetch all depts if creating new membership
    if (this.departments.length === 0) {
      await this.fetchDepartments();
    }
    await this.fetchPriceSheets();
    if (!this.accountingCategories.length) {
      await this.fetchAccountingCategories();
      await this.fetchAccountingClasses();
    }
    this.setFormData();
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      formData: {
        name: { required, $autoDirty: true },
        duration: { required, $autoDirty: true }
      },
      price: { required, $autoDirty: true }
    };
  },
  methods: {
    ...mapActions([
      "fetchMembershipProgram",
      "fetchPriceSheets",
      "fetchDepartments",
      "createMembershipProgram",
      "updateMembershipProgram",
      "addMembershipTasks",
      "fetchAccountingCategories",
      "fetchAccountingClasses"
    ]),
    setFormData() {
      let newFormData = { ...this.formData };
      if (this.currentMembershipProgram) {
        newFormData = { ...this.formData, ...this.currentMembershipProgram };
        this.price = this.currentMembershipProgram.price_cents;
        this.additionalUnitPrice =
          this.currentMembershipProgram.additional_unit_price_cents;
      }

      if (!newFormData.price_sheets) {
        newFormData.price_sheets = this.departments.map((dept) => ({
          department_id: dept.id,
          department_name: dept.name,
          department_membership_price_sheet_id: null
        }));
      }
      // Filter out archived departments
      newFormData.price_sheets = (newFormData.price_sheets || []).filter(
        (price) =>
          !this.departments.find((d) => d.id === price.department_id)?.archived
      );

      if (!newFormData.items) newFormData.items = [];

      this.formData = newFormData;
    },
    mapTaskToItem(task) {
      const { id, name } = task;
      return {
        item_id: id,
        item_type: "Task",
        unit_name: name,
        quantity: 1,
        frequency: {
          count: 1,
          period: "year"
        }
      };
    },
    returnToMembershipsPage() {
      window.location.href = this.membershipProgramIndexPath();
    },
    isLastOnRow(index) {
      return (index + 1) % 3 === 0;
    },
    toggleTaskModal() {
      this.taskModal = !this.taskModal;
    },
    getDeptPriceSheetOptions(deptId) {
      if (!this.priceSheets[deptId]) {
        return [];
      }
      return this.priceSheets[deptId].map((sheet) => ({
        value: sheet.id,
        label: `${sheet.name} ${sheet.set_as_standard ? " (Standard)" : ""} ${
          sheet.active ? "" : "(inactive)"
        }`
      }));
    },
    getPriceSheetLabel(deptId, priceSheetId) {
      if (!this.priceSheets[deptId]) {
        return "";
      }
      if (!priceSheetId) {
        return "Standard Price Sheet";
      }
      return this.priceSheets[deptId].find((sheet) => sheet.id === priceSheetId)
        .name;
    },
    handleDeptChange(deptId, priceSheetId) {
      this.formData.price_sheets = this.formData.price_sheets.map((dp) => {
        if (dp.department_id === deptId) {
          return {
            ...dp,
            department_membership_price_sheet_id: priceSheetId
          };
        }
        return dp;
      });
    },
    handleSave() {
      const upsert = this.currentMembershipProgram
        ? this.updateMembershipProgram
        : this.createMembershipProgram;
      const payload = {
        membership: {
          ...this.formData,
          price_cents: this.price,
          additional_unit_price_cents: this.additionalUnitPrice
        }
      };
      if (this.currentMembershipProgram && this.currentMembershipProgram.id) {
        payload.membership.id = this.currentMembershipProgram.id;
      }
      this.v$.$validate().then((valid) => {
        if (valid) {
          this.saving = true;
          upsert(payload)
            .then(() => {
              window.notify.success("Membership saved!");
              this.saving = false;
              this.returnToMembershipsPage();
            })
            .catch(() => (this.saving = false));
        } else {
          console.log("non valid");
        }
      });
    },
    addTaskToMembership(newTask) {
      const item = this.mapTaskToItem(newTask);
      const sameTask = this.formData.items.find(
        (task) => task.item_id === item.item_id
      );
      if (sameTask) {
        return window.notify.error(
          `Unable to add duplicate task to membership program.`
        );
      }
      window.notify.success(
        `${item.unit_name} has successfully been added to membership program.`
      );
      this.formData = {
        ...this.formData,
        items: [...this.formData.items, item]
      };
    },
    deleteItemFromMembership(itemId) {
      const remainingItems = this.formData.items.filter(
        (item) => item.item_id !== itemId
      );
      this.formData = {
        ...this.formData,
        items: remainingItems
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.flex-2 {
  flex: 2;
}

.department-container {
  display: flex;
  flex-wrap: wrap;
}

.button-container {
  display: flex;
  justify-content: space-between;
}

.price-sheet {
  margin-right: 1rem;
  min-width: 200px;
  max-width: 242px;
}

.last-pricesheet {
  margin-right: 0px;
}

.membership-card {
  max-width: 815px;
}

.tooltip-text {
  white-space: normal;
}

.membership-additional-info {
  display: flex;
  justify-content: center;
  :deep(.form-group) {
    margin-bottom: 0;
  }
}

// TODO: We need to fix the styling on included tasks. This is a bandaid.
:deep(.row-container) {
  margin-bottom: 1em;
  .form-group {
    margin-bottom: 0;
  }
  align-items: center;
  .unit-name {
    margin-top: 0;
  }
  .thumbnail-trash {
    margin-top: 0;
  }
  .textfield-label {
    width: auto !important;
    margin-bottom: 0;
  }
}
</style>
